/**
 * Rejilla semanal del horario (presentacional).
 *
 * Réplica del `<table>` del SSR (`schedule.html`): filas por hora x 7 días (lunes a
 * domingo) más las filas de almuerzo. Cada celda con órdenes es un botón coloreado
 * (verde/amarillo/rojo/gris según las pendientes, ver `cellTone`) que, al pulsarlo,
 * abre el detalle del slot (lo gestiona el padre con `onOpenSlot`). Sin lógica de
 * datos: recibe la rejilla ya cargada y notifica la apertura de un slot.
 */
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@/components/ui";
import { cn } from "@/components/ui";

import { DAY_HEADERS } from "../constants";
import { cellLabel, cellTone, type CellTone } from "../format";
import type { ScheduleRow } from "../types";

/** Estilos del botón de celda por tono (mismos colores que la leyenda del SSR). */
const TONE_CLASSES: Record<Exclude<CellTone, "empty">, string> = {
  low: "bg-emerald-500 text-white hover:bg-emerald-600",
  medium: "bg-amber-400 text-slate-900 hover:bg-amber-500",
  high: "bg-red-500 text-white hover:bg-red-600",
  delivered: "bg-slate-400 text-white hover:bg-slate-500",
};

interface ScheduleGridProps {
  rows: ScheduleRow[];
  /** Se invoca con (fecha ISO, hora) al pulsar una celda con órdenes. */
  onOpenSlot: (date: string, time: string) => void;
}

export function ScheduleGrid({ rows, onOpenSlot }: ScheduleGridProps) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell className="w-24">Hora</TableHeaderCell>
          {DAY_HEADERS.map((day) => (
            <TableHeaderCell key={day} className="text-center">
              {day}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          if (row.is_break) {
            return (
              <TableRow key={`break-${row.hour}`} className="bg-amber-50">
                <TableCell
                  colSpan={DAY_HEADERS.length + 1}
                  className="text-center text-sm font-semibold text-amber-700"
                >
                  {row.hour}
                </TableCell>
              </TableRow>
            );
          }

          return (
            <TableRow key={row.hour}>
              <TableCell className="font-semibold text-slate-700">
                {row.hour}
              </TableCell>
              {(row.days ?? []).map((cell, index) => {
                const tone = cellTone(cell);
                const dayName = DAY_HEADERS[index];
                if (tone === "empty") {
                  return (
                    <TableCell key={cell.date} className="text-center text-slate-300">
                      0
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={cell.date} className="text-center">
                    <button
                      type="button"
                      onClick={() => onOpenSlot(cell.date, row.hour)}
                      aria-label={`Ver órdenes del ${dayName} ${cell.date_display} a las ${row.hour}`}
                      className={cn(
                        "inline-flex min-w-12 items-center justify-center rounded-full px-3 py-1 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1",
                        TONE_CLASSES[tone],
                      )}
                    >
                      {cellLabel(cell)}
                    </button>
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
